/**
 * 単位の欄（#164、docs/66 §5）。見積・請求書の明細行（PC・スマホ）・写真の引き出しの採寸・拾い出し・品目の既定の単位・{vendorQuote}の行で同じ部品。
 *   ・欄に入ると会社の単位の一覧（設定 → 単位。並び順どおり）を候補に出す。品目の既定の単位（preferred）があれば先頭に
 *   ・打つと、その頭の文字で始まる単位だけに絞る（contract の unitCandidates。「m」で m・㎡・㎥）
 *   ・一覧に無い単位もそのまま打てる（打った文字が単位。一覧には足さない）
 *   ・↓↑ で候補を選ぶ・Enter で決める（選んでいなければ打った文字のまま。行の中では Enter のあと次の欄へ＝見積の表の決まりのまま）・Esc で候補を閉じる
 *   ・候補の枠は画面に対して置く（表の横に流れる枠・シートで切れない）。押した候補は mousedown で欄の focus を外さない
 * 行には単位を文字で保存する（今までどおり）。value・onChange は文字
 * #222：options を渡すと、会社の単位の代わりにその一覧を候補に出す（設定「明細の列」の 名称・規格・備考 の欄。候補のプルダウン＋自由入力の同じ形）
 */
import { unitCandidates } from "@genbaryoku/contract";
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useUnitNames } from "../lib/units.js";

export function UnitInput({
  id,
  value,
  onChange,
  onBlur,
  onKeyDown,
  preferred,
  className = "inp",
  label = "単位",
  placeholder,
  disabled,
  maxLength = 20,
  autoFocus,
  testId,
  style,
  options,
}: {
  id?: string | undefined;
  value: string;
  onChange: (v: string) => void;
  onBlur?: (() => void) | undefined;
  /** 欄の Enter などを外でも見る（変換確定の Enter を止める guardComposingEnter など）。候補を選んだ Enter のあとにも呼ぶ */
  onKeyDown?: ((e: ReactKeyboardEvent<HTMLInputElement>) => void) | undefined;
  /** 品目の既定の単位（先頭に出す） */
  preferred?: string | null | undefined;
  className?: string | undefined;
  /** 読み上げの名前（欄の名前） */
  label?: string | undefined;
  /** 浮く名前の欄（Field）の中では " " を渡す（字は出ない） */
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  maxLength?: number | undefined;
  autoFocus?: boolean | undefined;
  testId?: string | undefined;
  style?: React.CSSProperties | undefined;
  /** 候補の一覧（省けば会社の単位。#222） */
  options?: readonly string[] | undefined;
}) {
  const unitNames = useUnitNames();
  const units = options ?? unitNames;
  const [open, setOpen] = useState(false);
  /** 欄に入ってから打ったか（打つまでは全部を出す） */
  const [typed, setTyped] = useState(false);
  const [hi, setHi] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number; maxHeight: number } | null>(null);
  const listId = useId();
  const cands = unitCandidates(units, typed ? value : "", preferred);
  const shown = open && cands.length > 0;

  const close = () => {
    setOpen(false);
    setHi(-1);
  };
  const choose = (u: string) => {
    onChange(u);
    setTyped(false);
    close();
  };
  const place = () => {
    const r = input.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(120, r.width);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const below = vh - r.bottom - 12;
    const above = r.top - 12;
    // 下に 5 つ分（約 230px）入らず、上の方が広ければ上に出す
    if (below < 230 && above > below) setPos({ bottom: window.innerHeight - r.top + 4, left, width, maxHeight: Math.min(320, above) });
    else setPos({ top: r.bottom + 4, left, width, maxHeight: Math.min(320, Math.max(120, below)) });
  };
  useLayoutEffect(() => {
    if (shown) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, value]);
  useEffect(() => {
    if (!shown) return;
    const onDoc = (e: PointerEvent) => {
      if (input.current?.contains(e.target as Node) || pop.current?.contains(e.target as Node)) return;
      close();
    };
    const onMove = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return; // 候補の枠の中のスクロール
      place();
    };
    document.addEventListener("pointerdown", onDoc, true);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  // 選んでいる候補を枠の中で見える所へ
  useEffect(() => {
    if (hi >= 0) pop.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [hi]);

  const optId = (i: number) => `${listId}-o${i}`;
  return (
    <>
      <input
        ref={input}
        id={id}
        className={className}
        style={style}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-label={label}
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={shown && hi >= 0 ? optId(hi) : undefined}
        data-testid={testId}
        onFocus={() => {
          setTyped(false);
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setTyped(true);
          setOpen(true);
          setHi(-1);
        }}
        onBlur={() => {
          // 候補を押している最中（mousedown → blur）は押した候補に任せる
          window.setTimeout(() => {
            if (pop.current?.contains(document.activeElement)) return;
            close();
          }, 0);
          onBlur?.();
        }}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing || e.keyCode === 229) {
            onKeyDown?.(e);
            return;
          }
          if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !e.altKey) {
            if (!cands.length) return;
            e.preventDefault();
            e.stopPropagation();
            if (!shown) {
              setOpen(true);
              setHi(e.key === "ArrowDown" ? 0 : cands.length - 1);
              return;
            }
            setHi((h) => (e.key === "ArrowDown" ? (h + 1) % cands.length : (h <= 0 ? cands.length - 1 : h - 1)));
            return;
          }
          if (e.key === "Enter") {
            const c = shown && hi >= 0 ? cands[hi] : undefined;
            if (c != null) choose(c);
            else close();
            onKeyDown?.(e);
            return;
          }
          if (e.key === "Escape" && shown) {
            e.preventDefault();
            e.stopPropagation();
            close();
            return;
          }
          if (e.key === "Tab") close();
          onKeyDown?.(e);
        }}
      />
      {shown && pos
        ? createPortal(
            <div
              ref={pop}
              className="unitpop"
              id={listId}
              role="listbox"
              aria-label={label}
              data-testid="unit-options"
              style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
            >
              {cands.map((u, i) => (
                <button
                  key={u}
                  id={optId(i)}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  data-i={i}
                  aria-selected={i === hi}
                  className={"unitopt" + (i === hi ? " is-active" : "") + (u === value ? " is-current" : "")}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    choose(u);
                    input.current?.focus();
                  }}
                  data-testid="unit-option"
                >
                  {u}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
